<template>
  <section class="seo-links" v-if="narrowLinks && narrowLinks.items.length">
    <h2>{{ narrowLinks.title }}:</h2>
    <div>
      <input
        id="checkbox-narrow-links"
        ref="showCheckbox"
        class="hidden-checkbox"
        type="checkbox"
        aria-hidden="true"
        tabindex="-1"
      />
      <template
        v-for="item in narrowLinks.items.slice(0, INITIAL_ITEMS_DISPLAY)"
        :key="item.url"
      >
        <nuxt-link :to="item.url">
          {{ item.name
          }}<span v-if="showCounts" class="count">({{ item.count }})</span>
        </nuxt-link>
      </template>

      <template v-if="narrowLinks.items.length > INITIAL_ITEMS_DISPLAY">
        <div class="additional-content">
          <template
            v-for="item in narrowLinks.items.slice(INITIAL_ITEMS_DISPLAY)"
            :key="item.url"
          >
            <nuxt-link :to="item.url">
              {{ item.name
              }}<span v-if="showCounts" class="count">({{ item.count }})</span>
            </nuxt-link>
          </template>
        </div>

        <label for="checkbox-narrow-links" :aria-label="t('View more')">
          <translate class="section-close-txt">View fewer</translate>
          <translate class="section-open-txt">View more</translate>
        </label>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import type { SearchParty } from '../schemas'

interface NarrowLinksItem {
  count: number
  name: string
  url: string
  hierarchy_url: string
}

interface NarrowLinks {
  title: string
  items: NarrowLinksItem[]
}

interface Props {
  hierarchyPath?: string
  categoryIds: number[]
  facets?: string[]
  showCounts?: boolean
  party?: SearchParty | null
}

const props = withDefaults(defineProps<Props>(), {
  showCounts: true,
})

const { t } = useI18n()
const route = useRoute()

// Constants
const INITIAL_ITEMS_DISPLAY = 6

// Helper functions for building query parameters
function buildPartyParams(
  party: SearchParty | null | undefined,
  params: URLSearchParams,
) {
  if (!party) return

  // Only include adults if not 2 (default)
  if (party.adults) {
    params.append('adults', party.adults.toString())
  }
  // Child ages as comma-separated string
  if (party.childAges?.length) {
    params.append('child_ages', party.childAges.join(','))
  }
  // Dogs only if present
  if (party.dogs) {
    params.append('dogs', party.dogs.toString())
  }
}

function buildCategoryAndFacetParams(props: Props, params: URLSearchParams) {
  // Add category IDs as type params
  props.categoryIds.forEach((id) => {
    params.append('type', id.toString())
  })

  // Add facets if any
  if (props.facets?.length) {
    props.facets.forEach((facet) => {
      params.append('facet', facet)
    })
  }
}

// Build query params
const queryString = computed(() => {
  const params = new URLSearchParams()

  if (props.hierarchyPath) {
    params.append('path', props.hierarchyPath)
  }

  buildCategoryAndFacetParams(props, params)
  buildPartyParams(props.party, params)

  return params.toString()
})

// Build the API URL using the language fallback helper
const apiUrl = computed(() => {
  const lang = (route.params.lang as string) ?? 'en-gb'
  return getLangFallbackUrl(
    lang,
    `/search2/engagement-narrow-links/?${queryString.value}`,
  )
})

// Fetch narrow links data with SSR support
const { data: narrowLinks } = await useFetch(() => apiUrl.value, {
  key: `narrow-links-${apiUrl.value}`,
  // Use the same baseURL logic as in the ofetch plugin
  baseURL: import.meta.client
    ? ''
    : process.env.NUXT_AXIOS_BASE_URL || 'http://varnish_plus:8100',
  transform: (response: {
    engagement_narrow_links: NarrowLinks | null
  }): NarrowLinks | null => response.engagement_narrow_links,
  watch: [apiUrl],
})
</script>

<style scoped>
@import url('~/assets/css/seo-links.css');
</style>
